import cloneDeep from 'lodash/cloneDeep';
import { checkSessionToken, removeSessionToken } from 'src/utils/helpers';
import { maxCountLoadingMoreBtn, defaultCurrencyCode } from 'src/utils/config';
import apiService from 'src/utils/apiService';
import router from 'src/router';
import * as types from '../mutation-types';

// initial state
const state = {
  recentLogins: [],
  loginInfo: null,
  recentLoginsTotalCount: 0,
  notificationsHistoryList: [],
  notificationsHistoryTotalCount: 0,
  settings: null,
};

// getters
const getters = {
  userIsAdmin(s) {
    return !!s.loginInfo && s.loginInfo.decodedData.role === 'admin';
  },
  currencyCode(s) {
    return s.settings ? s.settings.currencyCode : defaultCurrencyCode;
  },
  settingsIsLoaded(s) {
    return !!s.settings;
  },
};

// actions
const actions = {
  updateUserSettings({ commit, state, getters }) { // eslint-disable-line
    return apiService.getUserSettings().then((data) => {
      commit(types.SET_USER_SETTINGS, data);
    });
  },
  changeEmailSubscriptionFrequency({ commit, state, getters }, val) {// eslint-disable-line
    return apiService.changeEmailSubscriptionFrequency(val).then(() => {
      commit(types.SET_EMAIL_SUBSCRIPTION_FREQUENCY, val);
    });
  },
  changeDefaultFiatCurrency({ commit, state, getters }, val) {// eslint-disable-line
    return apiService.changeDefaultFiatCurrency(val).then(() => {
      commit(types.SET_DEFAULT_FIAT_CURRENCY, val);
    });
  },
  changeTimezone({ commit, state, getters }, val) {// eslint-disable-line
    return apiService.changeTimezone(val).then(() => {
      commit(types.SET_TIMEZONE, val);
    });
  },
  changeEmailSubscription({ commit, state, getters }, val) {// eslint-disable-line
    return apiService.updateEmailSubscription(val).then(() => {
      commit(types.SET_EMAIL_SUBSCRIPTION, val);
    });
  },
  loadMoreNotificationsHistory({ commit, state }, notifications) { // eslint-disable-line
    return apiService.getNotificationsHistory(notifications, state.notificationsHistoryList.length).then((data) => {  // eslint-disable-line
      commit(types.ADD_MORE_NOTIFICATIONS_HISTORY, data.rows);
    });
  },
  updateNotificationsHistory({ commit, state }, notifications) { // eslint-disable-line
    return apiService.getNotificationsHistory(notifications).then((data) => {
      commit(types.UPDATE_NOTIFICATIONS_HISTORY, data.rows);
      commit(types.UPDATE_NOTIFICATIONS_HISTORY_TOTAL_COUNT, data.totalRowsCount);
    });
  },
  checkUserAuthSession({ commit, state }) {// eslint-disable-line
    const loginInfo = checkSessionToken();
    const newToken = loginInfo && loginInfo.token;
    const oldToken = state.loginInfo && state.loginInfo.token;
    const loginWasUpdated = newToken !== oldToken;
    if (loginWasUpdated) {
      commit(types.SET_LOGIN_INFO, loginInfo);
    }
  },
  logOut({ commit }) {
    removeSessionToken();
    router.push('/');
    commit(types.REMOVE_LOGIN_INFO);
  },
  updateRecentLogins({ commit }, limit) {
    return apiService.getRecentLogins(limit).then((recentLogins) => {
      commit(types.SET_RECENT_LOGINS, recentLogins.rows);
      commit(types.SET_RECENT_LOGINS_TOTAL_COUNT, recentLogins.totalRowsCount);
    });
  },
  loadMoreRecentLogins({ commit, state }) { // eslint-disable-line
    return apiService.getRecentLogins(maxCountLoadingMoreBtn, state.recentLogins.length).then((recentLogins) => { // eslint-disable-line
      commit(types.ADD_MORE_RECENTLOGINS, recentLogins.rows);
    });
  },
};

function initSettings(s) {
  if (!s.settings) {
    s.settings = {};// eslint-disable-line
  }
}

// mutations
const mutations = {
  [types.SET_USER_SETTINGS](s, settings) {
    s.settings = cloneDeep(settings); // eslint-disable-line
  },
  [types.SET_EMAIL_SUBSCRIPTION_FREQUENCY](s, frequency) {
    initSettings(s);
    s.settings.emailOverviewFrequency = frequency; // eslint-disable-line
  },
  [types.SET_EMAIL_SUBSCRIPTION](s, val) {
    initSettings(s);
    s.settings.emailSubscription = val; // eslint-disable-line
  },
  [types.SET_DEFAULT_FIAT_CURRENCY](s, frequency) {
    initSettings(s);
    s.settings.currencyCode = frequency; // eslint-disable-line
  },
  [types.SET_TIMEZONE](s, timezone) {
    initSettings(s);
    s.settings.timezone = timezone; // eslint-disable-line
  },
  [types.SET_RECENT_LOGINS](s, recentLogins) {
    s.recentLogins = cloneDeep(recentLogins); // eslint-disable-line
  },
  [types.SET_LOGIN_INFO](s, loginInfo) {
    s.loginInfo = cloneDeep(loginInfo); // eslint-disable-line
  },
  [types.REMOVE_LOGIN_INFO](s) {
    // Clear all user data
    s.loginInfo = null; // eslint-disable-line
    s.recentLogins = []; // eslint-disable-line
    s.recentLoginsTotalCount = 0; // eslint-disable-line
    s.notificationsHistoryList = []; // eslint-disable-line
    s.notificationsHistoryTotalCount = null; // eslint-disable-line
    s.settings = null; // eslint-disable-line
    this.state.tickers.userFavoriteTickersList = [];
    this.state.tickers.totalRowsCountFavortiteTickers = null;
    this.state.tickers.tickersUpdateDate = null;
    this.state.alerts.alertsList = [];
    this.state.alerts.alertGroupedByTickers = [];
    this.state.subscription.tariffs = [];
    this.state.subscription.tariffPendingPayment = null;
    this.state.userCurrencies.currenciesList = [];
    this.state.userNotifications.userNotifications = [];
    this.state.platformNotifications.platformNotifications = [];
  },
  [types.UPDATE_NOTIFICATIONS_HISTORY](s, notifications) {// eslint-disable-line
    s.notificationsHistoryList = notifications; // eslint-disable-line
  },
  [types.UPDATE_NOTIFICATIONS_HISTORY_TOTAL_COUNT](s, totalCount) {// eslint-disable-line
    s.notificationsHistoryTotalCount = totalCount; // eslint-disable-line
  },
  [types.SET_RECENT_LOGINS_TOTAL_COUNT](s, totalCount) {// eslint-disable-line
    s.recentLoginsTotalCount = totalCount; // eslint-disable-line
  },
  [types.ADD_MORE_RECENTLOGINS](s, recentLogins) {
    s.recentLogins = [...s.recentLogins, ...cloneDeep(recentLogins)]; // eslint-disable-line
  },
  [types.ADD_MORE_NOTIFICATIONS_HISTORY](s, notifications) {
    s.notificationsHistoryList = [...s.notificationsHistoryList, ...cloneDeep(notifications)]; // eslint-disable-line
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/userData.js